<script setup lang="ts">
import { debounce } from "lodash-es";
import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";

import theCard from "@/components/theCard.vue";
import theProductCard from "@/components/theProductCard.vue";
import { getCoursePaths } from "@/services/catalogService/courses";
import { EventBus } from "@/store/eventBus";
import getDuration from "@/utils/getDuration";
import isUnauth from "@/utils/isUnauth";
import { toggleMyListItem } from "@/utils/myListActions";
import telemetry from "@/utils/telemetry";

const props = withDefaults(
  defineProps<{
    result?: any;
    index?: number;
    status?: any;
    forCarousel?: boolean;
    cardView?: string;
    cardStyle?: string;
    priceView?: string;
    topicView?: boolean;
    isStoreContent?: boolean;
    showDescription?: boolean;
  }>(),
  {
    result: () => ({}),
    index: 0,
    status: null,
    forCarousel: false,
    cardView: "Grid",
    cardStyle: "Grid",
    priceView: "USD",
    topicView: false,
    isStoreContent: false,
    showDescription: true,
  }
);

const store = useStore();
const router = useRouter();
const route = useRoute();

const paths = ref<any>(null);

const inMyList = computed(() => store.getters["user/inMyList"]);
const freeId = computed(() => store.getters["content/freeId"]);

const freeContent = computed(() =>
  props.result?.content_bundle?.includes(freeId.value)
);

const showPremium = computed(() => {
  if (globalThis.env.ENABLE_LOCK_CONTENT) {
    return Boolean(props.result?.lock_content);
  }
  return !props.status?.userEntitlements?.includes(props.result?.guid);
});

const resultLink = computed(() => {
  const practiceExamId = props.result?.friendly_url || props.result?.id;

  return practiceExamId
    ? router.resolve({ name: "practice-exam", params: { practiceExamId } }).href
    : "";
});

const bookmarkMe = (content: any) => {
  toggleMyListItem(content, content.type, {
    inMyList: inMyList.value,
    addContentToMyList: (payload: any) =>
      store.dispatch("user/addContentToMyList", payload),
    removeContentFromMyList: (payload: any) =>
      store.dispatch("user/removeContentFromMyList", payload),
    route,
  });
};

const debouncedBookmarkMe = debounce(
  (content: any) => bookmarkMe(content),
  500
);

const sendTelemetry = async () => {
  let telemetryResponse = telemetry.external(
    {
      action: "launch-contentType",
      url: resultLink.value,
      title: props.result.title,
      id: props.result.id,
      page: route.fullPath,
      type: props.result.type,
      relevance: props.result.relevance,
      sortOrder: props.index,
    },
    isUnauth()
  );
  telemetryResponse
    .then(() => {
      router.push(resultLink.value);
    })
    .catch(() => {
      // do nothing
    });
};

const openCartModal = () => {
  EventBus.emit("openCartModal", {
    data: paths.value,
    courseCard: true,
  });
};

const getCoursePaths_ = async (id: number) => {
  await getCoursePaths(id)
    .then((data) => {
      paths.value = data?.paths;
    })
    .catch(() => {
      router.push({ name: "error" });
    });
};

defineExpose({
  paths,
  freeContent,
  showPremium,
  resultLink,
  addContentToMyList: (payload: any) =>
    store.dispatch("user/addContentToMyList", payload),
  removeContentFromMyList: (payload: any) =>
    store.dispatch("user/removeContentFromMyList", payload),
  bookmarkMe,
  debouncedBookmarkMe,
  openCartModal,
  getCoursePaths: getCoursePaths_,
  sendTelemetry,
  isUnauth,
  getDuration,
});
</script>

<template>
  <div
    :class="[
      'relative z-10 h-full hover:z-[700] focus:z-[700] active:z-[700]',
      {
        'w-[19rem]': props.cardView === 'Grid' && !topicView,
        'w-full': props.cardView !== 'Grid' && !topicView,
        'w-[22rem]': topicView,
      },
    ]"
    data-cy="practice-exam-result"
    v-show="result"
  >
    <the-card
      v-if="!isStoreContent"
      :is-unauth="isUnauth()"
      :for-carousel="forCarousel"
      :card-view="props.cardView"
      :card-style="props.cardStyle"
      :result="result"
      :status="status"
      :link="resultLink"
      :cy="'practice-exam-card'"
      :show-description="props.showDescription"
      @navigate="sendTelemetry"
      @bookmark="debouncedBookmarkMe(result)"
    />
    <the-product-card
      v-else
      :is-unauth="isUnauth()"
      :card-view="props.cardView"
      :card-style="props.cardStyle"
      :price-view="priceView"
      :result="result"
      :status="status"
      :cy="'practice-exam-product-card'"
      :link="resultLink"
      :show-description="props.showDescription"
      @navigate="sendTelemetry"
      @bookmark="debouncedBookmarkMe(result)"
    />
  </div>
</template>
